import { Firm } from "pages/analyze-stock";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import CandleChart from "src/common/charts/candleChart";
import ChartCard from "src/common/charts/ChartCard";
import HpbChart from "src/common/charts/hpbChart";
import LineChart from "src/common/charts/lineChart";
import RibbonChart from "src/common/charts/ribbonChart";
import RsiLineChart from "src/common/charts/rsiLineChart";
import VolumeCompareBarChart from "src/common/charts/volumeCompareBarChart";
import { hasVolumeCompareData } from "src/common/charts/volumeCompareChart.utils";
import DialogModal from "src/common/DialogModal";
import DialogModalNoTitle from "src/common/DialogModalNoTitle";
import PerformanceTable from "src/components/analyze-stock/PerformanceTable";
import AppContext from "src/components/AppContext";
import { SectorMomentumHeader } from "src/components/sector-momentum/SectorMomentumColorScale";
import ChartSkeleton from "src/components/skeleton/ChartSkeleton";
import PerformanceTableSkeleton from "src/components/skeleton/PerformanceTableSkeleton";
import {
  candleDetailsCardWrapperClass,
  DataPoint,
  fetchWrapper,
  getCandleDetailsTitle,
  renderCandleDetailsHtml,
  renderCandleDetailsReact,
} from "src/helpers";
import {
  generateSectorColor,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import {
  computeSectorRanks,
  getOrderedSectorsByLastWeek,
  getSectorMomentumDateRange,
  getSectorMomentumHighlightWeek,
  type SectorMomentumData,
} from "src/helpers/sectorMomentum";
import { useStockCharts } from "src/hooks/useStockCharts";
import { useVolumeAnalysis } from "src/hooks/useVolumeAnalysis";
import { SearchContext } from "../../common/search";
import moment from "moment-timezone";
import { useNonWorkingDays } from "src/hooks/useNonWorkingDays";

interface ChartsProps {
  symbols: string;
  formattedDate: string | undefined;
  isDashboard?: boolean;
}

const BackgroundFreezer: React.FC<{
  freeze: boolean;
  children: React.ReactNode;
}> = ({ freeze, children }) => {
  const content = useRef(children);
  if (!freeze) {
    // eslint-disable-next-line react-hooks/refs
    content.current = children;
  }
  // eslint-disable-next-line react-hooks/refs
  return <>{content.current}</>;
};

const Charts = (props: ChartsProps) => {
  const { symbols, formattedDate, isDashboard } = props;
  const user = getLocalStorageUser();
  const token = user.token;
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const [toggleChart, setToggleChart] = useState<boolean>(false);
  const [firms, setFirms] = useState<Firm[]>([]);
  const searchContext = useContext(SearchContext);
  const { selectedCompany, isLoading: isDashboardLoading } = searchContext;

  const [toggleRsiChart, setToggleRsiChart] = useState<boolean>(false);
  const [loadingRibbon, setLoadingRibbon] = useState(true);
  const [dataSymbolRibbon, setDataSymbolRibbon] = useState<string | null>(null);
  const [sectorMomentumData, setSectorMomentumData] =
    useState<SectorMomentumData>({});
  const [highlightedSectors, setHighlightedSectors] = useState<string[]>([]);
  const [selectedLegendSectors, setSelectedLegendSectors] = useState<string[]>(
    [],
  );

  const nonWorkingDays = useNonWorkingDays(
    isDashboard ? "lastSourceDate" : undefined,
  );
  const [volumeDate, setVolumeDate] = useState<Date | null>(null);
  const [volumeAnchorDate, setVolumeAnchorDate] = useState<Date | null>(null);

  useEffect(() => {
    if (formattedDate) {
      const d = moment.tz(formattedDate, "Asia/Kolkata").startOf("day");
      let latestWorking = moment().tz("Asia/Kolkata").startOf("day");

      const isNonWorking = (m: moment.Moment) =>
        m.day() === 0 ||
        m.day() === 6 ||
        (Array.isArray(nonWorkingDays) &&
          nonWorkingDays.some((h) => moment(h).isSame(m, "day")));

      while (isNonWorking(latestWorking)) {
        latestWorking.subtract(1, "day");
      }

      let anchor = d.isAfter(latestWorking) ? latestWorking : d;
      while (isNonWorking(anchor)) {
        anchor.subtract(1, "day");
      }

      setVolumeAnchorDate(anchor.toDate());
    }
  }, [formattedDate, nonWorkingDays]);

  const volumeHighlightedDates = useMemo(() => {
    if (!volumeAnchorDate) return [];
    const dates: string[] = [];
    let current = moment(volumeAnchorDate).tz("Asia/Kolkata");

    while (dates.length < 10) {
      const isWeekend = current.day() === 0 || current.day() === 6;
      const isHoliday = nonWorkingDays.some((d) =>
        moment(d).isSame(current, "day"),
      );

      if (!isWeekend && !isHoliday) {
        dates.push(current.format("YYYY-MM-DD"));
      }
      current.subtract(1, "day");
    }
    return dates;
  }, [volumeAnchorDate, nonWorkingDays]);

  useEffect(() => {
    if (volumeHighlightedDates.length > 0) {
      setVolumeDate(
        moment.tz(volumeHighlightedDates[0], "Asia/Kolkata").toDate(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volumeAnchorDate]);

  const { rankedData, orderedSectors, sectorColors, filteredMomentumData } =
    useMemo(() => {
      const ranks = computeSectorRanks(sectorMomentumData);
      const ordSectors = getOrderedSectorsByLastWeek(ranks);

      const sColors: Record<
        string,
        ReturnType<typeof generateSectorColor>
      > = {};
      ordSectors.forEach((sector, idx) => {
        sColors[sector] = generateSectorColor(idx, ordSectors.length);
      });

      return {
        rankedData: ranks,
        orderedSectors: ordSectors,
        sectorColors: sColors,
        filteredMomentumData: sectorMomentumData,
      };
    }, [sectorMomentumData]);

  const legendSectors = useMemo(
    () =>
      highlightedSectors
        .filter((sector) => orderedSectors.includes(sector))
        .sort((a, b) => orderedSectors.indexOf(a) - orderedSectors.indexOf(b)),
    [highlightedSectors, orderedSectors],
  );

  const chartHighlightedSectors =
    selectedLegendSectors.length > 0 ? selectedLegendSectors : legendSectors;

  const sectorMomentumHighlightWeek = useMemo(
    () => getSectorMomentumHighlightWeek(filteredMomentumData, formattedDate),
    [filteredMomentumData, formattedDate],
  );

  const handleToggleLegendSector = (sector: string) => {
    setSelectedLegendSectors((prev) => {
      const isSelected = prev.includes(sector);
      if (isSelected) {
        const next = prev.filter((s) => s !== sector);
        return next.length === 0 ? legendSectors : next;
      }
      return [...prev, sector];
    });
  };

  useEffect(() => {
    setSelectedLegendSectors(legendSectors);
  }, [legendSectors]);

  const [expandChart, setExpandChart] = useState<string>("");
  const [expandChartTitle, setExpandChartTitle] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [loadingFirms, setLoadingFirms] = useState(false);
  const [dataSymbolFirms, setDataSymbolFirms] = useState<string | null>(null);
  const {
    chartData,
    historicalDates,
    rsiData,
    hpbData,
    hpbYear,
    loading: loadingCharts,
    setHoveredCandle,
    activeCandle,
  } = useStockCharts(
    selectedCompany.symbol,
    token,
    false,
    false,
    false,
    true,
    formattedDate,
    isDashboard,
    true,
    true,
    isDashboardLoading,
    true,
    !!isDashboard,
  );

  const handleHistoricalPointerClick = useCallback((dateStr: string) => {
    const newDate = moment.tz(dateStr, "Asia/Kolkata").toDate();
    setVolumeDate(newDate);
    setVolumeAnchorDate(newDate);
  }, []);

  const selectedVolumeDateStr = volumeDate
    ? moment.tz(volumeDate, "Asia/Kolkata").format("YYYY-MM-DD")
    : formattedDate;

  const { volumeCompareSnapshot, loadingVolume } = useVolumeAnalysis(
    selectedCompany.symbol,
    selectedVolumeDateStr,
    token,
    isDashboardLoading,
  );

  const hasSectorMomentumData = Object.keys(filteredMomentumData).length > 0;

  const hpbTitle = useMemo(
    () =>
      `Predicting Stock Movement over each month from HpB${hpbYear ? " since " + hpbYear : ""}`,
    [hpbYear],
  );

  const isLoadingRibbon =
    isDashboardLoading ||
    loadingRibbon ||
    (!!selectedCompany.symbol && dataSymbolRibbon !== selectedCompany.symbol);
  const showSectorMomentumToggle =
    !isLoadingRibbon && !loadingCharts && hasSectorMomentumData;

  const sectorMomentumHeader =
    orderedSectors.length > 0 ? (
      <SectorMomentumHeader
        orderedSectors={orderedSectors}
        legendSectors={legendSectors}
        selectedSectors={selectedLegendSectors}
        sectorColors={sectorColors}
        onToggleSector={handleToggleLegendSector}
      />
    ) : null;

  useEffect(() => {
    if (!isLoadingRibbon && !hasSectorMomentumData) {
      setToggleRsiChart(true);
    }
  }, [isLoadingRibbon, hasSectorMomentumData]);

  const renderChart = (type: string, fullSize = false) => {
    switch (type) {
      case "Line":
        return (
          <LineChart
            lineData={chartData}
            symbolName={selectedCompany.symbol}
            darkMode={!!darkMode}
            isAnalyzeStock={true}
            isExpand={fullSize}
          />
        );
      case "Rsi":
        return (
          <RsiLineChart
            rsiData={rsiData}
            darkMode={!!darkMode}
            isExpand={fullSize}
          />
        );
      case "Ribbon":
        if (isLoadingRibbon) {
          return <ChartSkeleton />;
        }
        if (Object.keys(filteredMomentumData).length === 0) {
          return (
            <div className="text-gray-500 text-center py-25">No data found</div>
          );
        }
        return (
          <div className="w-full h-full overflow-hidden">
            <div className="w-full h-full [&>div]:!h-full">
              <RibbonChart
                selectedSector={""}
                highlightedSectors={chartHighlightedSectors}
                highlightWeek={sectorMomentumHighlightWeek}
                sectorColors={sectorColors}
                sectors={orderedSectors}
                rankingData={filteredMomentumData}
                rankedData={rankedData}
                darkMode={!!darkMode}
                showLegend={false}
                compact={!fullSize}
              />
            </div>
          </div>
        );
      case "Hpb":
        return (
          <HpbChart
            hpbChart={hpbData}
            darkMode={!!darkMode}
            isExpand={fullSize}
            isDashboard={!fullSize}
          />
        );

      case "VolumeCompare":
        return (
          <VolumeCompareBarChart
            snapshot={volumeCompareSnapshot}
            darkMode={!!darkMode}
            isDashboard={!fullSize}
            isExpand={fullSize}
            symbolName={selectedCompany.symbol}
            dateOptions={volumeHighlightedDates}
            selectedDate={volumeDate}
            onDateChange={setVolumeDate}
          />
        );

      case "Candle":
        return (
          <CandleChart
            candleData={chartData}
            symbolName={selectedCompany.symbol}
            darkMode={!!darkMode}
            isExpand={fullSize}
            onHoverChange={setHoveredCandle}
            historicalDates={historicalDates}
            onHistoricalPointerClick={handleHistoricalPointerClick}
          />
        );
      case "Table": {
        const isLoadingFirms =
          isDashboardLoading ||
          loadingFirms ||
          (!!symbols && dataSymbolFirms !== symbols);
        if (isLoadingFirms)
          return <PerformanceTableSkeleton isDashboardChart={true} />;
        return (
          <PerformanceTable
            firms={firms}
            isResearchFirm={true}
            isDashboardChart={!fullSize}
            score={0}
            fullSize={fullSize}
          />
        );
      }
      default:
        return <></>;
    }
  };

  const getFirms = async (signal: AbortSignal) => {
    try {
      setLoadingFirms(true);
      const response = await fetchWrapper.get(
        `/api/dashboard/researchfirms?symbol=${encodeURIComponent(symbols)}`,
        undefined,
        { Authorization: `Bearer ${token}` },
        signal,
      );
      if (signal.aborted) return;
      if (Array.isArray(response.data)) {
        setFirms(response.data);
        setDataSymbolFirms(symbols);
      } else {
        setFirms([]);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") return;
      console.error("Error fetching fundamentals:", error);
      setFirms([]);
    } finally {
      if (!signal.aborted) setLoadingFirms(false);
    }
  };

  useEffect(() => {
    if (isDashboardLoading) return;
    const controller = new AbortController();
    getFirms(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.symbol, symbols, isDashboardLoading]);

  useEffect(() => {
    if (isDashboardLoading) return;
    if (!formattedDate || !selectedCompany.symbol) {
      setSectorMomentumData({});
      setHighlightedSectors([]);
      setSelectedLegendSectors([]);
      setLoadingRibbon(false);
      return;
    }

    const getSectorMomentumData = async (signal: AbortSignal) => {
      setLoadingRibbon(true);
      setToggleRsiChart(false);
      try {
        const { fromDate, toDate } = getSectorMomentumDateRange(formattedDate);
        const response = await fetchWrapper.get(
          `/api/dashboard/sector-momentum?fromDate=${fromDate}&toDate=${toDate}&symbol=${encodeURIComponent(selectedCompany.symbol)}`,
          undefined,
          { Authorization: `Bearer ${token}` },
          signal,
        );

        if (signal.aborted) return;

        setSectorMomentumData(response.data ?? {});
        setHighlightedSectors(response.sectors ?? []);
        setDataSymbolRibbon(selectedCompany.symbol);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;
        console.error("Error fetching sector momentum:", error);
        setSectorMomentumData({});
        setHighlightedSectors([]);
      } finally {
        if (!signal.aborted) setLoadingRibbon(false);
      }
    };

    const controller = new AbortController();
    getSectorMomentumData(controller.signal);
    return () => controller.abort();
  }, [formattedDate, selectedCompany.symbol, token, isDashboardLoading]);

  return (
    <>
      <BackgroundFreezer freeze={showModal}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-1">
          <ChartCard
            isDashboardChart
            title={
              <div className="flex min-w-0 flex-col md:flex-row md:items-center gap-1.5 md:gap-3">
                <span className="shrink-0 text-sm md:text-base whitespace-nowrap font-medium">
                  1 Year Chart (TF: 1 Day)
                </span>
                {!toggleChart && chartData.length > 0 && (
                  <div
                    className={`${candleDetailsCardWrapperClass} max-[1100px]:hidden`}
                    title={getCandleDetailsTitle(activeCandle as DataPoint)}
                  >
                    {renderCandleDetailsReact(activeCandle as DataPoint)}
                  </div>
                )}
              </div>
            }
            ontoggle={true}
            toggleChart={toggleChart}
            setToggleChart={setToggleChart}
            onExpand={
              !loadingCharts && chartData.length > 0
                ? () => {
                    setExpandChart(toggleChart ? "Line" : "Candle");
                    setExpandChartTitle("1 Year Chart (TF: 1 Day)");
                    setShowModal(true);
                  }
                : undefined
            }
          >
            {loadingCharts ? (
              <ChartSkeleton />
            ) : chartData.length === 0 ? (
              <div className="text-gray-500 text-center py-25">
                No data found
              </div>
            ) : (
              renderChart(toggleChart ? "Line" : "Candle")
            )}
          </ChartCard>
          <ChartCard
            title={toggleRsiChart ? "RSI Trend" : "Sector Momentum"}
            headerAccessory={!toggleRsiChart ? sectorMomentumHeader : undefined}
            isDashboardChart
            ontoggle={showSectorMomentumToggle}
            toggleChart={toggleRsiChart}
            setToggleChart={setToggleRsiChart}
            onExpand={
              !loadingCharts &&
              (toggleRsiChart ? rsiData.length > 0 : hasSectorMomentumData)
                ? () => {
                    setExpandChart(toggleRsiChart ? "Rsi" : "Ribbon");
                    setExpandChartTitle(
                      toggleRsiChart ? "RSI Trend" : "Sector Momentum",
                    );
                    setShowModal(true);
                  }
                : undefined
            }
          >
            {loadingCharts || (!toggleRsiChart && isLoadingRibbon) ? (
              <ChartSkeleton />
            ) : !toggleRsiChart ? (
              hasSectorMomentumData ? (
                renderChart("Ribbon")
              ) : (
                <div className="text-gray-500 text-center py-25">
                  No data found
                </div>
              )
            ) : rsiData.length === 0 ? (
              <div className="text-gray-500 text-center py-25">
                No data found
              </div>
            ) : (
              renderChart("Rsi")
            )}
          </ChartCard>
        </div>

        <div className="mb-2 grid min-w-0 grid-cols-1 gap-1 lg:grid-cols-2 xl:grid-cols-3">
          <ChartCard
            title={hpbTitle}
            isDashboardChart
            isHPB
            compactHeader
            wrapTitle
            className="min-w-0"
            onExpand={
              !loadingCharts && hpbData.length > 0
                ? () => {
                    setExpandChart("Hpb");
                    setExpandChartTitle(hpbTitle);
                    setShowModal(true);
                  }
                : undefined
            }
          >
            {loadingCharts ? (
              <ChartSkeleton />
            ) : hpbData.length === 0 ? (
              <div className="text-gray-500 text-center py-25">
                No data found
              </div>
            ) : (
              renderChart("Hpb")
            )}
          </ChartCard>

          <ChartCard
            title="Volume Analysis"
            isDashboardChart
            compactHeader
            className="min-w-0"
            headerAccessory={undefined}
            onExpand={
              hasVolumeCompareData(volumeCompareSnapshot)
                ? () => {
                    setExpandChart("VolumeCompare");
                    setExpandChartTitle("Volume Analysis");
                    setShowModal(true);
                  }
                : undefined
            }
          >
            {loadingVolume ? <ChartSkeleton /> : renderChart("VolumeCompare")}
          </ChartCard>

          <ChartCard
            title="Research insights"
            compactHeader
            isDashboardChart
            hideSymbol
            className="min-w-0 lg:col-span-2 xl:col-span-1"
            onExpand={
              !(loadingFirms || !firms.length) && firms.length > 0
                ? () => {
                    setExpandChart("Table");
                    setExpandChartTitle("Research Firm Insights");
                    setShowModal(true);
                  }
                : undefined
            }
          >
            {renderChart("Table")}
          </ChartCard>
        </div>
      </BackgroundFreezer>

      {expandChart === "Table" ? (
        <DialogModalNoTitle
          message={renderChart(expandChart, true)}
          show={showModal}
          setShow={setShowModal}
        />
      ) : (
        <DialogModal
          isChart={true}
          headerAccessory={
            expandChart === "Ribbon" ? sectorMomentumHeader : undefined
          }
          title={`
            <div class="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4">
              <span class="whitespace-nowrap">${selectedCompany.symbol}: ${expandChartTitle}</span>
              ${!chartData || chartData.length === 0 || expandChart !== "Candle" ? "" : renderCandleDetailsHtml(activeCandle as DataPoint)}
            </div>
          `}
          message={renderChart(expandChart, true)}
          show={showModal}
          setShow={setShowModal}
        />
      )}
    </>
  );
};

export default React.memo(Charts);
